<template>
  <div
    ref="rootEl"
    class="establishment-multi-filter">
    <button
      type="button"
      class="establishment-multi-filter__trigger"
      :class="{ 'establishment-multi-filter__trigger--open': open }"
      :aria-expanded="open"
      @click="toggleOpen">
      <span class="establishment-multi-filter__trigger-label">{{ props.triggerLabel }}</span>
      <span
        v-if="props.modelValue.length"
        class="establishment-multi-filter__count"
        >({{ props.modelValue.length }})</span
      >
      <svg
        class="establishment-multi-filter__chev"
        :class="{ 'establishment-multi-filter__chev--open': open }"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true">
        <path
          d="M2 4l4 4 4-4"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round" />
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-show="open && panelPositioned"
        ref="panelEl"
        class="establishment-multi-filter__panel establishment-multi-filter__panel--floating"
        role="dialog"
        :aria-label="props.triggerLabel"
        :style="panelFloatingStyle">
        <div class="establishment-multi-filter__panel-inner">
          <template v-if="props.mode === 'flat'">
            <div class="establishment-multi-filter__chip-grid">
              <button
                v-for="opt in props.flatOptions"
                :key="opt.slug"
                type="button"
                class="establishment-multi-filter__chip-btn"
                @click="toggle(opt.slug)">
                <PrePeersTag
                  :label="opt.label"
                  appearance="chip"
                  :selected="isOn(opt.slug)" />
              </button>
            </div>
          </template>
          <template v-else>
            <PrePeersAccordionTagSections
              density="compact"
              expand-mode="exclusive"
              :sections="groupedAccordionSections"
              :is-item-selected="isGroupedAccordionItemSelected"
              :section-badge-count="groupedSectionBadgeCount"
              :initial-open-section-ids="groupedInitialOpenSectionIds"
              @toggle-item="onGroupedAccordionToggle" />
          </template>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { ComputedRef, CSSProperties, Ref } from 'vue'

import PrePeersAccordionTagSections from '~/components/core/PrePeersAccordionTagSections.vue'
import PrePeersTag from '~/components/core/PrePeersTag.vue'
import type {
  PrePeersAccordionTagSection,
  PrePeersAccordionTagTogglePayload,
} from '~/types/PrePeersAccordionTagSections'

import type { EstablishmentMultiSelectFilterProps } from '~/types/EstablishmentMultiSelectFilter'

/**
 * Définit les props du composant EstablishmentMultiSelectFilter.
 */
const props = withDefaults(defineProps<EstablishmentMultiSelectFilterProps>(), {
  flatOptions: () => [],
  sections: () => [],
})

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
}>()

const groupedAccordionSections: ComputedRef<readonly PrePeersAccordionTagSection[]> = computed(
  (): readonly PrePeersAccordionTagSection[] =>
    (props.sections ?? []).map(
      (sec): PrePeersAccordionTagSection => ({
        id: sec.sectionSlug,
        title: sec.sectionLabel,
        items: sec.options.map((o) => ({ id: o.slug, label: o.label })),
      }),
    ),
)

const groupedInitialOpenSectionIds: ComputedRef<string[]> = computed((): string[] => {
  const sections = props.sections ?? []
  const first = sections[0]
  return first !== undefined ? [first.sectionSlug] : []
})

const open: Ref<boolean> = ref<boolean>(false)
const panelPositioned: Ref<boolean> = ref<boolean>(false)
const rootEl: Ref<HTMLElement | null> = ref<HTMLElement | null>(null)
const panelEl: Ref<HTMLElement | null> = ref<HTMLElement | null>(null)
const panelFloatingStyle: Ref<CSSProperties> = ref<CSSProperties>({})

let panelResizeObserver: ResizeObserver | null = null
const scrollParentEls: HTMLElement[] = []

/**
 * Conteneurs scrollables ancêtres du déclencheur (colonne aside, etc.).
 * @param {HTMLElement | null} el Élément racine du filtre.
 * @returns {HTMLElement[]} Liste des parents avec overflow scroll/auto.
 */
function getScrollParentElements(el: HTMLElement | null): HTMLElement[] {
  const parents: HTMLElement[] = []
  let node: HTMLElement | null = el?.parentElement ?? null
  while (node !== null) {
    const style: CSSStyleDeclaration = getComputedStyle(node)
    const overflowY: string = style.overflowY
    const overflowX: string = style.overflowX
    if (/(auto|scroll|overlay)/.test(`${overflowY}${overflowX}`)) {
      parents.push(node)
    }
    node = node.parentElement
  }
  return parents
}

/**
 * @returns {void}
 */
function detachScrollListeners(): void {
  window.removeEventListener('scroll', syncPanelPosition, true)
  window.removeEventListener('resize', syncPanelPosition)
  for (const parent of scrollParentEls) {
    parent.removeEventListener('scroll', syncPanelPosition)
  }
  scrollParentEls.length = 0
}

/**
 * @returns {void}
 */
function attachScrollListeners(): void {
  detachScrollListeners()
  window.addEventListener('scroll', syncPanelPosition, true)
  window.addEventListener('resize', syncPanelPosition)
  for (const parent of getScrollParentElements(rootEl.value)) {
    parent.addEventListener('scroll', syncPanelPosition, { passive: true })
    scrollParentEls.push(parent)
  }
}

/**
 * Recale `left` pour que le panneau `fixed` reste dans le viewport.
 * @returns {void}
 */
function clampFloatingPanelHorizontal(): void {
  if (!open.value || !rootEl.value || !panelEl.value) return
  const trigger: DOMRect = rootEl.value.getBoundingClientRect()
  const pad: number = 8
  const vw: number = window.innerWidth
  const pw: number = panelEl.value.getBoundingClientRect().width
  let left: number = trigger.left
  if (left + pw > vw - pad) {
    left = vw - pad - pw
  }
  if (left < pad) {
    left = pad
  }
  panelFloatingStyle.value = {
    ...panelFloatingStyle.value,
    left: `${left}px`,
  }
}

/**
 * Aligne le panneau sous le déclencheur (`fixed` + Teleport) pour passer au-dessus du CTA Lola fixe.
 * Bandes min / max alignées sur l'ancien panneau `absolute` : `min(360px, 92vw)` × `min(480px, 96vw)`,
 * pour que la grille de tags repasse bien à la ligne (pas de `max-content` sur le conteneur).
 * @returns {void}
 */
function syncPanelPosition(): void {
  if (!open.value || !rootEl.value) {
    panelFloatingStyle.value = {}
    return
  }
  const trigger: DOMRect = rootEl.value.getBoundingClientRect()
  const pad: number = 8
  const vw: number = window.innerWidth
  const vh: number = window.innerHeight
  const top: number = trigger.bottom + 6
  const maxH: number = Math.max(120, Math.min(520, vh - top - pad))
  const maxWCap: number = Math.min(560, Math.max(240, vw - 2 * pad))
  const legacyMin: number = Math.min(360, 0.92 * vw)
  const legacyMax: number = Math.min(480, 0.96 * vw)
  const triggerFloor: number = Math.max(240, trigger.width)
  const minWidthPx: number = Math.floor(Math.min(maxWCap, Math.max(legacyMin, triggerFloor)))
  const maxWidthPx: number = Math.floor(Math.min(maxWCap, legacyMax, vw - 2 * pad))
  const minWClamped: number = Math.min(minWidthPx, maxWidthPx)

  panelFloatingStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${trigger.left}px`,
    minWidth: `${minWClamped}px`,
    maxWidth: `${maxWidthPx}px`,
    width: 'auto',
    maxHeight: `${maxH}px`,
    zIndex: 1000,
  }

  requestAnimationFrame((): void => {
    clampFloatingPanelHorizontal()
  })
}

/**
 * Ferme le panneau si le clic est en dehors du déclencheur et du panneau téléporté.
 * @param {MouseEvent} event - Clic extérieur.
 * @returns {void}
 */
function onDocClick(event: MouseEvent): void {
  if (!open.value || !rootEl.value) return
  const t: EventTarget | null = event.target
  if (!(t instanceof Node)) return
  if (rootEl.value.contains(t)) return
  if (panelEl.value?.contains(t)) return
  open.value = false
}

onMounted((): void => {
  document.addEventListener('click', onDocClick, true)
})

onBeforeUnmount((): void => {
  document.removeEventListener('click', onDocClick, true)
  detachScrollListeners()
  panelResizeObserver?.disconnect()
  panelResizeObserver = null
})

watch(open, async (isOpen: boolean): Promise<void> => {
  if (isOpen) {
    panelPositioned.value = false
    await nextTick()
    syncPanelPosition()
    await nextTick()
    syncPanelPosition()
    panelPositioned.value = true
    attachScrollListeners()
    if (typeof ResizeObserver !== 'undefined' && panelEl.value !== null) {
      panelResizeObserver?.disconnect()
      panelResizeObserver = new ResizeObserver((): void => {
        syncPanelPosition()
      })
      panelResizeObserver.observe(panelEl.value)
    }
  } else {
    detachScrollListeners()
    panelResizeObserver?.disconnect()
    panelResizeObserver = null
    panelPositioned.value = false
    panelFloatingStyle.value = {}
  }
})

/**
 * @returns {void}
 */
function toggleOpen(): void {
  open.value = !open.value
}

/**
 * @param {string} slug - Slug option.
 * @returns {boolean} true si le slug est dans la sélection.
 */
function isOn(slug: string): boolean {
  return props.modelValue.includes(slug)
}

/**
 * @param {string} slug - Slug à basculer.
 * @returns {void}
 */
function toggle(slug: string): void {
  const next: string[] = isOn(slug)
    ? props.modelValue.filter((s: string) => s !== slug)
    : [...props.modelValue, slug]
  emit('update:modelValue', next)
}

/**
 * @param {string} _sectionId - Slug de rubrique (non utilisé pour l'état URL).
 * @param {string} itemId - Slug niveau.
 * @returns {boolean} true si le slug est sélectionné.
 */
function isGroupedAccordionItemSelected(_sectionId: string, itemId: string): boolean {
  return isOn(itemId)
}

/**
 * Nombre d'options sélectionnées dans une rubrique (bulle bleue sur le spoiler).
 * @param {string} sectionId - `sectionSlug`.
 * @returns {number} Nombre de slugs sélectionnés dans cette rubrique.
 */
function groupedSectionBadgeCount(sectionId: string): number {
  const sec = (props.sections ?? []).find((s) => s.sectionSlug === sectionId)
  if (!sec) return 0
  const inSection = new Set(sec.options.map((o) => o.slug))
  return props.modelValue.filter((slug: string) => inSection.has(slug)).length
}

/**
 * @param {PrePeersAccordionTagTogglePayload} payload - Section + item.
 * @returns {void}
 */
function onGroupedAccordionToggle(payload: PrePeersAccordionTagTogglePayload): void {
  toggle(payload.itemId)
}
</script>

<style scoped>
.establishment-multi-filter {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.establishment-multi-filter__trigger {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.55em 0.9em;
  border: 1.5px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-elevated);
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: var(--font-body);
  cursor: pointer;
  max-width: 100%;
}

.establishment-multi-filter__trigger--open {
  border-color: var(--accent-blue);
}

.establishment-multi-filter__trigger-label {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.establishment-multi-filter__count {
  color: var(--text-secondary);
  font-weight: 500;
}

.establishment-multi-filter__chev {
  flex-shrink: 0;
  margin-left: var(--space-1);
  color: var(--text-muted);
  transition: transform var(--duration-fast) var(--ease-out);
  display: block;
}

.establishment-multi-filter__chev--open {
  transform: rotate(180deg);
}

.establishment-multi-filter__panel {
  overflow: auto;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-default);
  background: var(--bg-surface);
  box-shadow: var(--shadow-lg);
}

.establishment-multi-filter__panel--floating {
  box-sizing: border-box;
}

.establishment-multi-filter__panel-inner {
  /* 10px à 16px root : milieu de l'échelle entre --space-2 et --space-3 */
  padding: calc((var(--space-2) + var(--space-3)) / 2);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  box-sizing: border-box;
  width: 100%;
}

.establishment-multi-filter__chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.establishment-multi-filter__chip-btn {
  display: inline-block;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;
}

.establishment-multi-filter__chip-btn:hover :deep(.prepeers-tag--chip.prepeers-tag--inactive) {
  border-color: var(--accent-blue);
  color: var(--text-primary);
}
</style>
